<template>

	<Drawer initialWidth="4xl" @requestUnmount="hideEditor">

		<template #header>
			<div class="font-semibold text-md w-full">
				{{ $tc('Goal',2) }}
				<div class="flex gap-2 w-full items-center justify-center">
					<!-- <PupilSymbol :pupil="pupil" class="w-12" /> -->
					<span class="font-bold text-lg">{{ pupil.getFullName() }}</span>
				</div>
			</div>
		</template>

		<template #default="{ hide }">
			<div class="px-2 pb-8">

				<div class="isolate">

					<div class="sticky top-0 z-30 bg-base">
						<div class="flex justify-between px-2 py-4">
							<div class="flex items-center gap-4">
								<!-- year selector -->
								<SelectRoot v-if="withSchoolYearSelector" v-model="schoolYearOffset">
									<SelectTrigger class="inline-flex items-center justify-between rounded px-4 py-1 bg-base hover:bg-base-100 outline-none min-w-40 text-sm">
										<SelectValue>{{ dateRangeLabel(schoolYearOffset) }}</SelectValue>
										<!-- Phosphor icon: caret-up-down -->
										<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 256 256">
											<path d="M181.66,170.34a8,8,0,0,1,0,11.32l-48,48a8,8,0,0,1-11.32,0l-48-48a8,8,0,0,1,11.32-11.32L128,212.69l42.34-42.35A8,8,0,0,1,181.66,170.34Zm-96-84.68L128,43.31l42.34,42.35a8,8,0,0,0,11.32-11.32l-48-48a8,8,0,0,0-11.32,0l-48,48A8,8,0,0,0,85.66,85.66Z"/>
										</svg>
									</SelectTrigger>
									<SelectPortal>
										<SelectContent class="z-50 bg-base rounded-md border shadow-lg">
											<SelectViewport>
												<SelectGroup>
													<SelectLabel class="px-4 py-2 text-xs uppercase text-base-content-light font-bold">{{ $t('School year') }}</SelectLabel>
													<SelectItem v-for="offset in [0, -1]" :key="offset" :value="offset"
														class="text-sm leading-none rounded-md flex items-center py-2 px-4 relative select-none data-[highlighted]:outline-none data-[highlighted]:bg-base-100 hover:cursor-pointer"
														:class="[schoolYearOffset === offset? 'font-bold': '']"
													>
														<SelectItemText as="div" class="flex flex-col gap-1">
															<span>{{ dateRangeLabel(offset) }}</span>
															<span class="text-xs text-base-content-light font-normal">
																{{ offset === 0 && !showEvaluatedGoalsOnly ? $t('Covered goals') : $t('Evaluated goals') }}
															</span>
														</SelectItemText>
													</SelectItem>
												</SelectGroup>
											</SelectViewport>
										</SelectContent>
									</SelectPortal>
								</SelectRoot>

								<!-- "show notes" toggle -->
								<div class="flex items-center gap-2">
									<SwitchRoot
										v-model="showRecentNotes"
										id="showRecentNotes"
										@update:modelValue="showRecentNotes = $event"
										class="w-[32px] h-[20px] shadow-sm flex data-[state=unchecked]:bg-gray-300 data-[state=checked]:bg-gray-800 border border-gray-300 data-[state=checked]:border-stone-700 rounded-full relative transition-[background]"
									>
										<SwitchThumb class="w-3.5 h-3.5 my-auto bg-white text-xs flex items-center justify-center shadow-xl rounded-full transition-transform translate-x-0.5 will-change-transform data-[state=checked]:translate-x-full" />
									</SwitchRoot>
									<label for="showRecentNotes" class="hover:cursor-pointer">{{ $t('Show last notes of the evaluations') }}</label>
								</div>


							</div>
							<div>
								<button v-if="showPrintButton" type="button" class="btn btn-sm btn-ghost bg-base-100" @click="openReport()" :title="$t('Print report')">
									<PrinterIcon class="w-4 h-4" />
								</button>
							</div>
						</div>

						<!-- Only show the filter if no pre-defined goals are provided.
						     The drawer owns the goal scopes and the period (see constrainToSchoolYear), so the
						     filter's scope dropdown stays hidden: any choice made there would be overruled. -->
						<div class="px-4 pb-2 border-b" v-if="!goals && hasCurriculum">
							<GoalFilter
								v-if="loaded"
								:initialFilter="initialFilter"
								:goalSources="goalsStore.allSources"
								:collapsable="true"
								:collapsed="false"
								@change="applyFilter"
								:withSchedulingSegmentsOptions="false"
							/>
						</div>
						<slot name="topMessage"></slot>
					</div>

					<div v-if="!hasCurriculum" class="alert alert-warning m-4 whitespace-pre-line">
						{{ $t('#instruction: curriculum not set') }}
					</div>

					<div v-else-if="loading" class="flex justify-center py-16">
						<span class="loading loading-spinner loading-lg"></span>
					</div>

					<GoalsPupilsMatrix
						v-else
						ref="matrix"
						:goalStructures="goalStructures"
						:showEmptyState="false"
						:onlyGoals="true"
						:simpleBulkToggle="true"
						:bulkPupil="readOnly ? null : pupil"
						@selectionChange="onSelectionChange"
						@bulkEvaluate="openBulkEditor"
					>
						<template #goal="{ goal }">

							<div class="flex justify-between gap-4 items-start group">

								<div class="flex flex-col">
									<GoalTitle :goal="goal" :showPath="true" titleClasses=""/>
									<div v-if="showRecentNotes && recentEvaluationOf(goal)?.comment" class="ml-20">
										<div class="text-sm text-base-content-light mt-0.5">
											<div>{{ recentEvaluationOf(goal).author?.name }} - {{ formatEvaluationDate(recentEvaluationOf(goal)) }}</div>
											"<span class="italic">{{ recentEvaluationOf(goal).comment }}</span>"
										</div>
									</div>
								</div>

								<div class="w-fit shrink-0 flex justify-center gap-1.5 border border-base-200 rounded-full hover:scale-110 transition-all">
									<button type="button" class="flex gap-1 p-1" @click="evaluationFormGoal = goal">
										<div v-if="goal.hasEvaluation(pupil)" class="rounded-full w-8 h-8 grid place-content-center" :style="{ backgroundColor: goal.getEvaluation(pupil).evaluationLevel?.color }">
											<EllipsisHorizontalIcon class="w-4 h-4 invisible group-hover:visible" />
										</div>
										<div v-else class="rounded-full w-8 h-8 bg-gray-100 hover:bg-gray-400 group-hover:bg-gray-200 grid place-content-center">
											<EllipsisHorizontalIcon class="w-4 h-4 invisible group-hover:visible" />
										</div>
										<div v-if="readOnly">
											<!-- phosphor icon - clock-counter-clockwise -->
											<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#000000" viewBox="0 0 256 256">
												<path d="M132,80v45.74l38.06,22.83a4,4,0,0,1-4.12,6.86l-40-24A4,4,0,0,1,124,128V80a4,4,0,0,1,8,0Zm-4-44A91.42,91.42,0,0,0,62.93,63C53.05,73,44.66,82.47,36,92.86V64a4,4,0,0,0-8,0v40a4,4,0,0,0,4,4H72a4,4,0,0,0,0-8H40.47C49.61,89,58.3,79,68.6,68.6a84,84,0,1,1,1.75,120.49,4,4,0,1,0-5.5,5.82A92,92,0,1,0,128,36Z">
												</path>
											</svg>
										</div>
									</button>
								</div>

							</div>

						</template>
					</GoalsPupilsMatrix>
				</div>

				<div class="drawer-action-bar justify-end gap-2">
					<button type="button" class="btn btn-ghost" @click="hide">
						{{ $t('Close') }}
					</button>
				</div>

			</div>
		</template>

	</Drawer>

	<EvaluationEditor
		v-if="evaluationFormGoal"
		@requestClose="closeEvaluationForm()"
		:pupil="pupil"
		:goal="evaluationFormGoal"
		:readOnly="readOnly"
	></EvaluationEditor>

	<BulkEvaluationEditor
		v-if="showBulkEditor"
		:pairs="bulkPairs"
		:allPupils="bulkAllPupils"
		@saved="onBulkSaved"
		@requestClose="showBulkEditor = false"
	/>

</template>

<script lang="ts">

import Drawer from "@/components/ui/Drawer.v2.vue";
import { Goal } from "@/models/Goal.model";
import { Pupil } from "@/models/Pupil.model";
import { useEvaluationLevelsStore } from "@/stores/EvaluationLevels.store";
import { useEvaluationsStore } from "@/stores/Evaluations.store";
import {GOAL_SCOPES, GoalFilterDef} from "@/stores/filters/GoalFilterDef";
import { mapStores } from "pinia";
import { PropType } from "vue";
import { Classroom } from "@/models/Classroom.model";
import GoalsPupilsMatrix from "@/components/GoalsPupilsMatrix.vue";
import EvaluationEditor from "./EvaluationEditor.vue";
import BulkEvaluationEditor from "./BulkEvaluationEditor.vue";
import type { BulkEvaluationPair } from "./BulkEvaluationEditor.vue";
import GoalTitle from "@/components/GoalTitle.vue";
import PupilSymbol from "@/components/ui/symbols/PupilSymbol.vue";
import { EllipsisHorizontalIcon, PrinterIcon } from "@heroicons/vue/24/outline";
import {GoalSearchResult, useGoalsStore} from "@/stores/Goals.store.ts";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { Curriculum } from "@/models/Curriculum.model";
import GoalFilter from "@/components/GoalFilter.vue";
import {PopulatedGoalStructureContent} from "@/utils/GoalSource.ts";
import { EvaluationFilterDef } from "@/stores/filters/EvaluationFilterDef";
import { Evaluation } from "@/models/Evaluation.model";
import { DateTime } from "luxon";
import { SelectRoot, SelectContent, SelectItem, SelectItemText, SelectGroup, SelectLabel, SelectPortal, SelectTrigger, SelectValue, SelectViewport, SwitchRoot, SwitchThumb } from 'reka-ui';

export default {

	emits: [
		'requestClose',
	],

	props: {
		pupil: {
			type: Pupil,
			required: true,
		},
		goals: {
			type: Array as unknown as Goal[],
			required: false,
		},
		showEvaluatedGoalsOnly: {
			type: Boolean,
			default: false,
		},
		withSchoolYearSelector: {
			type: Boolean,
			default: false,
		},
		readOnly: {
			type: Boolean,
			default: false,
		},
		classroom: {
			type: Object as PropType<Classroom | null>,
			default: null,
		},
	},

	components: {
		GoalFilter,
		Drawer,
		GoalsPupilsMatrix,
		EvaluationEditor,
		BulkEvaluationEditor,
		GoalTitle,
		PupilSymbol,
		EllipsisHorizontalIcon, PrinterIcon,
		SelectRoot, SelectContent, SelectItem, SelectItemText, SelectGroup, SelectLabel, SelectPortal, SelectTrigger, SelectValue, SelectViewport,
		SwitchRoot, SwitchThumb
	},

	data() {
		return {
			loading: false,
			loaded: false,
			showRecentNotes: true,
			filter: null as GoalFilterDef | null,
			initialFilter: null as GoalFilterDef | null,
			evaluationFormGoal: null as Goal | null,
			schoolYearOffset: 0, // 0 = current school year, -1 = previous school year, etc.
			filteredPropGoals: null as Goal[] | null,
			showBulkEditor: false,
			bulkPairs: [] as BulkEvaluationPair[],
			bulkAllPupils: [] as Pupil[],
		}
	},

	computed: {
		...mapStores(
			useEvaluationLevelsStore,
			useEvaluationsStore,
			useGoalsStore,
			useOrganisationsStore
		),

		/** Goals are only ever consulted or evaluated for the school's own curriculum. */
		schoolCurriculum(): Curriculum | null {
			return this.organisationsStore.currentOrganisation?.curriculum ?? null;
		},

		hasCurriculum(): boolean {
			return this.schoolCurriculum !== null;
		},

		/** The supplied goals a teacher may evaluate: those of the school's curriculum. */
		filteredGoalsPropertyOnOrgCurriculum(): Goal[] | null {
			if (!this.goals) {
				return null;
			}
			return this.organisationsStore.curriculumGoals(this.goals as Goal[]);
		},

		goalStructures(): PopulatedGoalStructureContent[] {
			if(this.filteredPropGoals) {
				// Build structured views from the filtered prop goals, grouped per source.
				const searchResults = this.filteredPropGoals.map((g: Goal) => new GoalSearchResult(g));
				return this.goalsStore.calculateGoalStructures(searchResults);
			}

			return this.goalsStore.structuredGoals;
		},

		showPrintButton(): boolean {
			return !this.goals && this.hasCurriculum; // Only show the print button when no specific goals are provided, as the report is based on the currently applied filter
		},

		/** The shown school year, as configured for the organisation. Null when no school year start is set. */
		activeSchoolYearRange(): { start: Date, end: Date } | null {
			return this.getSchoolYearDateRange(this.schoolYearOffset);
		},

		/**
		 * Which goals are listed for the shown school year. This is decided by the drawer, not by the
		 * goal filter: the filter's own scopes are overruled by these (see constrainToSchoolYear).
		 *
		 * By default, both the goals COVERED (in the selected classroom) and the goals EVALUATED in the
		 * current school year are shown. For a previous school year, only the goals EVALUATED in that
		 * school year are shown.
		 */
		schoolYearGoalScopes(): GOAL_SCOPES[] {
			if (this.schoolYearOffset !== 0 || this.showEvaluatedGoalsOnly) {
				return [GOAL_SCOPES.EVALUATED];
			}
			return [GOAL_SCOPES.PLANNED, GOAL_SCOPES.EVALUATED];
		},
	},

	watch: {
		async schoolYearOffset() {
			// Nothing to reload without a filter: no curriculum, or supplied goals (not filtered by school year).
			if (!this.filter) {
				return;
			}

			this.scopeGoalsStoreToSchoolYear();
			await this.applyFilter(this.filter);
		},
	},

	/**
	 * The drawer works in one of two modes:
	 *  - supplied goals (`goals` prop): a fixed list, e.g. the goals of an observation. Their
	 *    structures and this pupil's evaluations are loaded once; there is no goal filter.
	 *  - browsing (no `goals` prop): the goals store is filtered by the goal filter, constrained
	 *    to the selected school year (see constrainToSchoolYear).
	 */
	async created() {
		// Without a curriculum there is nothing a teacher may evaluate: show the notice only.
		if (!this.hasCurriculum) {
			this.loaded = true;
			return;
		}

		this.loading = true;

		try {
			this.scopeGoalsStoreToSchoolYear();

			await this.evaluationLevelsStore.loadOnce();

			if (this.goals) {
				await this.showSuppliedGoals();
			} else {
				await this.browseGoals();
			}
		} catch (e) {
			this.$notify({ text: this.$t('The goals could not be loaded'), type: 'error' });
		} finally {
			this.loaded = true;
			this.loading = false;
		}
	},

	methods: {
		formatEvaluationDate(evaluation: Evaluation): string {
			if (!evaluation?.date) return '';
			return DateTime.fromJSDate(evaluation.date).toLocaleString(DateTime.DATE_MED);
		},

		/** This pupil's evaluation of the goal within the shown school year, if any. */
		recentEvaluationOf(goal: Goal): Evaluation | null {
			if (!this.activeSchoolYearRange) {
				return null;
			}
			return goal.getEvaluationInRange(this.pupil, this.activeSchoolYearRange.start, this.activeSchoolYearRange.end) ?? null;
		},

		/**
		 * Lesson plans may carry goals from other curricula (external plans, or plans kept
		 * after a curriculum switch). Teachers only ever see and evaluate the school's own
		 * curriculum, so the shared store runs in single-curriculum mode for the current
		 * school year. Evaluations of a previous school year were made against whatever
		 * curriculum the school used back then, so those are shown across all curricula.
		 */
		scopeGoalsStoreToSchoolYear() {
			this.goalsStore.setCurriculum(this.schoolYearOffset === 0 ? this.schoolCurriculum : null);
		},

		/** Supplied goals mode: structure the school's curriculum goals among the given ones and show this pupil's evaluations on them. */
		async showSuppliedGoals() {
			// Make sure the source structures are loaded, as we need them to show the goals
			await this.goalsStore.loadSourcesForGoals(this.filteredGoalsPropertyOnOrgCurriculum as Goal[]);
			// Load existing evaluations for this pupil so previous scores are shown
			await this.loadEvaluationsForPropGoals();
		},

		/** Browsing mode: build the default filter for this pupil and apply it. */
		async browseGoals() {
			const filter = this.goalsStore.getDefaultEvaluationsFilter(this.classroom);
			filter.pupilIds = [this.pupil.id as string];

			// The goal filter shows a "reset" button whenever its filter differs from the initial
			// one, so the initial filter carries the same school year constraints as the applied one.
			this.initialFilter = this.constrainToSchoolYear(filter);

			await this.applyFilter(this.initialFilter.clone());
		},

		/**
		 * Applies a filter coming from the goal filter (or the initial one), after constraining
		 * it to the selected school year. Shows the spinner while the goals are loading.
		 */
		async applyFilter(filter: GoalFilterDef) {
			this.filter = this.constrainToSchoolYear(filter);
			this.loading = true;

			try {
				await this.goalsStore.applyFilter(this.filter);
			} finally {
				this.loading = false;
			}
		},

		/**
		 * The school year selector is not part of the goal filter, so its period and goal scopes
		 * are (re)applied on top of whatever the goal filter produces.
		 */
		constrainToSchoolYear(filter: GoalFilterDef): GoalFilterDef {
			if (!this.activeSchoolYearRange) {
				throw new Error('No school year start date set for organisation');
			}

			filter.start = this.activeSchoolYearRange.start;
			filter.end = this.activeSchoolYearRange.end;
			filter.goalScopes = this.schoolYearGoalScopes;
			return filter;
		},

		async loadEvaluationsForPropGoals() {
			const goals = this.filteredGoalsPropertyOnOrgCurriculum;
			if (!this.pupil?.id || !goals?.length) return;

			const goalIds = goals
				.map((g: Goal) => g.id)
				.filter((id): id is string => Boolean(id));

			if (!goalIds.length) return;

			const filter = new EvaluationFilterDef();
			filter.pupilId = this.pupil.id;
			filter.setParamValues('goalIds', goalIds);

			await this.evaluationsStore.applyFilter(filter);

			const evaluations = (this.evaluationsStore.items || []).slice().reverse();
			evaluations.forEach((evaluation: Evaluation) => {
				if (!evaluation.pupilId || !evaluation.goalId) return;
				const goal = goals.find((g: Goal) => g.id === evaluation.goalId);
				if (goal) {
					goal.addEvaluation(evaluation.pupilId, evaluation);
				}
			});

			if(this.showEvaluatedGoalsOnly) {
				this.filteredPropGoals = goals.filter((goal: Goal) => {
					return goal.hasEvaluation(this.pupil);
				});
			} else {
				this.filteredPropGoals = goals;
			}
		},

		closeEvaluationForm() {
			this.evaluationFormGoal = null;
		},

		onSelectionChange(pairs: BulkEvaluationPair[]) {
			this.bulkPairs = pairs;
		},

		openBulkEditor(pairs: BulkEvaluationPair[], allPupils: Pupil[]) {
			this.bulkPairs = pairs;
			this.bulkAllPupils = allPupils;
			this.showBulkEditor = true;
		},

		onBulkSaved() {
			this.showBulkEditor = false;
			(this.$refs.matrix as any)?.exitBulkMode();
		},


		hideEditor () {
			this.$emit('requestClose');
		},

		openReport() {
			const dateRange = this.activeSchoolYearRange;
			if (!dateRange) {
				return;
			}

			const route = this.$router.resolve({
				name: 'pupil.report',
				params: { pupilId: this.pupil.id },
				query: {
					notes: this.showRecentNotes ? 'true' : 'false',
					// The report is built from its own filter, so it needs to know the period that is shown here.
					since: DateTime.fromJSDate(dateRange.start).toISODate(),
					until: DateTime.fromJSDate(dateRange.end).toISODate(),
				},
			});
			window.open(route.href, '_blank');
		},

		/**
		 * The school year at the given offset from the current one (0 = current, -1 = previous),
		 * as configured for the organisation. Null when no school year start is set.
		 */
		getSchoolYearDateRange(schoolYearOffset: number): { start: Date, end: Date } | null {
			const interval = this.organisationsStore.currentOrganisation?.getSchoolYearInterval(schoolYearOffset);
			if (!interval?.isValid || !interval.start || !interval.end) {
				return null;
			}
			return { start: interval.start.toJSDate(), end: interval.end.toJSDate() };
		},

		dateRangeLabel(schoolYearOffset: number): string {
			const range = this.getSchoolYearDateRange(schoolYearOffset);
			if (!range) {
				return '';
			}
			return `${range.start.getFullYear()} - ${range.end.getFullYear()}`;
		},
	},
}

</script>
